import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { Option } from "app/types";
import clsx from "clsx";
import React, { useRef, useState } from "react";
import CircularLoader from "../CircularLoader";
import { CheckIcon } from "../Icons";
import CheckMark from "../Icons/Checkmark";
import SearchIcon from "../Icons/SearchIcon";
import Input from "../Input";
import { SelectMenuItemProps } from "./types";

const DropdownMenuContentComponent: React.FC<SelectMenuItemProps<Option[]>> = ({
  options,
  menuContentClassName,
  menuListItemLabelClassName,
  menuListItemClassName,
  allowSearch,
  searchPlaceholder,
  onChange,
  unPaddedRole,
  setOpen,
  align,
  dropdownWidth,
  showEndIcon,
  value,
  hasMore,
  loading,
  onLoadMore,
  isPagination,
  showRightIcon,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [search, setSearch] = useState("");
  const filteredOptions = searchFilter(options, search);
  function searchFilter(
    options: Option[] | undefined,
    keyword: string,
  ): Option[] {
    if (!keyword || !options) {
      return options ? options : [];
    }

    const lowerCaseKeyword = keyword.toLowerCase();

    return options?.filter((option) => {
      return option.label.toLowerCase().includes(lowerCaseKeyword);
    });
  }
  const handleScroll = () => {
    if (!ref.current || loading || !hasMore) return;
    const { scrollTop, scrollHeight, clientHeight } = ref.current;

    if (scrollTop + clientHeight >= scrollHeight * 0.9) {
      onLoadMore?.();
    }
  };
  return (
    <>
      <DropdownMenu.Content
        align={align}
        sideOffset={5}
        side="bottom"
        style={{ width: dropdownWidth }}
        className={`bg-white shadow-lg rounded-md z-50 w-64 border border-[#E9ECF2]  py-2 `}
      >
        {allowSearch && (
          <div className="">
            <Input
              noBorder
              id="search"
              icon={<SearchIcon size="17" color="#79808A" />}
              placeholder={searchPlaceholder}
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
              }}
              className=""
              innerContainerClassName="px-2 border-b border-gray-100 "
            />
          </div>
        )}
        <div
          className={clsx("overflow-auto max-h-[270px] ", menuContentClassName)}
          onScroll={handleScroll}
          ref={ref}
        >
          {filteredOptions.map((option) => (
            <DropdownMenu.Item
              key={option.value}
              disabled={option.disabled}
              onSelect={(event) => {
                if (option.disabled) {
                  event.preventDefault();
                  return;
                }
                onChange(option.value);
                setOpen(false);
              }}
              className={clsx(
                "text-sm leading-[21px] font-normal text-secondary px-[8px] pl-[16px] py-[10px] flex items-center gap-2 overflow-y-auto outline-none",
                option.disabled
                  ? "cursor-not-allowed opacity-50"
                  : "cursor-pointer hover:bg-[#F4F5F7]",
                menuListItemClassName,
              )}
            >
              <div className="flex flex-1 items-start gap-2">
                {option.icon && (
                  <div className="min-w-[20px] min-h-[20px]">{option.icon}</div>
                )}
                <div className="flex flex-col w-full">
                  <div className="flex items-center justify-between gap-1">
                    <span
                      className={`text-sm leading-[21px] font-normal text-black ${menuListItemLabelClassName}`}
                    >
                      {option.label}
                    </span>
                    {showRightIcon && value === option.value && (
                      <span
                        className={`text-sm leading-[21px] font-normal text-black ${menuListItemLabelClassName}`}
                      >
                        <CheckMark size={16} />
                      </span>
                    )}
                  </div>
                  {option.description && (
                    <span
                      className={`text-xs leading-[16px] font-normal text-secondary mt-1`}
                    >
                      {option.description}
                    </span>
                  )}
                  {option.role && (
                    <span
                      className={`text-xs leading-[16px] font-normal text-secondary ${
                        unPaddedRole ? "" : "ml-8"
                      } mt-1`}
                    >
                      {option.role}
                    </span>
                  )}
                </div>
              </div>
              {showEndIcon && value === option.value && (
                <CheckIcon color="#5A8DF1" />
              )}
            </DropdownMenu.Item>
          ))}
          {/* Loader or "No more data" */}
          {loading && (
            <div className="py-2 text-center text-xs text-gray-400">
              <CircularLoader size="sm" />
            </div>
          )}
          {isPagination && !hasMore && !loading && (
            <div className="py-2 text-center text-xs text-gray-400">
              No more options
            </div>
          )}
        </div>
      </DropdownMenu.Content>
    </>
  );
};

export default DropdownMenuContentComponent;
